<!-- ============================================================
     NOTIFICATION CENTRE  —  Add · Edit · Manage
     ============================================================ -->
<div class="notif-page">

  <!-- ── Toast ── -->
  @if (toastMsg()) {
    <div class="notif-toast alert alert-success d-flex align-items-center gap-2 shadow">
      <i class="bi bi-check-circle-fill fs-5"></i><strong>{{ toastMsg() }}</strong>
    </div>
  }

  <!-- ── Delete-All Confirm ── -->
  @if (showDeleteAll()) {
    <div class="notif-overlay" (click)="showDeleteAll.set(false)">
      <div class="notif-modal" (click)="$event.stopPropagation()">
        <div class="nm-header del-header">
          <div class="nm-icon"><i class="bi bi-trash3-fill"></i></div>
          <div><h5 class="nm-title">Clear Notifications?</h5><p class="nm-sub">{{ getFilterLabel() | lowercase }}</p></div>
          <button class="nm-close" (click)="showDeleteAll.set(false)"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="nm-body p-4 text-center">
          <p class="text-muted small mb-0">Permanently delete <strong>{{ filtered() && filtered().length }}</strong> notification(s).</p>
        </div>
        <div class="nm-footer">
          <button class="btn btn-outline-secondary px-4" (click)="showDeleteAll.set(false)">Cancel</button>
          <button class="btn btn-danger px-4" (click)="deleteAll()"><i class="bi bi-trash3 me-2"></i>Clear All</button>
        </div>
      </div>
    </div>
  }

  <!-- ===================== PAGE HEADER ===================== -->
  <div class="notif-ph mb-4">
    <div>
      <h1 class="notif-title"><i class="bi bi-bell-fill me-2 text-primary" style="font-size:1.1rem;"></i>Notification Centre</h1>
      <p class="notif-subtitle">
        <span class="unread-count-badge">{{ unreadCount() }} unread</span>
        &nbsp;·&nbsp; {{ notifications() && notifications().length }} total
      </p>
    </div>
    <div class="d-flex gap-2 flex-wrap align-items-start">
      <!-- @if (unreadCount() > 0) {
        <button class="btn btn-outline-primary btn-sm d-flex align-items-center gap-2" (click)="markAllRead()">
          <i class="bi bi-check-all"></i><span class="d-none d-sm-inline">Mark All Read</span>
        </button>
      }
      @if (filtered() && filtered().length && !showForm()) {
        <button class="btn btn-outline-danger btn-sm d-flex align-items-center gap-2" (click)="showDeleteAll.set(true)">
          <i class="bi bi-trash3"></i><span class="d-none d-sm-inline">Clear</span>
        </button>
      } -->
      @if (!showForm()) {
        <button class="btn btn-outline-success btn-sm" (click)="openAddForm()">
          <i class="bi bi-plus-circle-fill me-1"></i>Add Notification
        </button>
      }
      @if (showForm()) {
        <button class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" (click)="closeForm()">
          <i class="bi bi-arrow-left"></i>Back to List
        </button>
      }
    </div>
  </div>

  <!-- ===================== ADD / EDIT FORM ===================== -->
  @if (showForm()) {
    <div class="form-layout">

      <!-- Left: Form Fields -->
      <div class="form-fields-panel">
        <div class="ffp-header">
          <div class="ffp-icon" [style.background]="selectedColorGradient()">
            <i class="bi {{ form.icon ?? 'bi-bell-fill' }}"></i>
          </div>
          <div>
            <h5 class="ffp-title">{{ isEditMode() ? 'Edit Notification' : 'Add New Notification' }}</h5>
            <p class="ffp-sub">Fill in the fields below. Use the live preview on the right.</p>
          </div>
        </div>

        <div class="ffp-body">

          <!-- ── MESSAGE ── -->
          <div class="field-group">
            <label class="field-label">Notification Message <span class="req">*</span></label>
            <textarea class="form-control"
              [(ngModel)]="form.ntext"
              rows="3" maxlength="250"
              placeholder="e.g. New admission application received from Arjun Sharma…"
              [class.is-invalid]="formSubmitted && formErrors['text']">
            </textarea>
            <div class="d-flex justify-content-between mt-1">
              @if (formErrors['text']) {
                <div class="invalid-feedback d-block">{{ formErrors['text'] }}</div>
              }
              <small class="text-muted ms-auto">{{ (form.ntext ?? '').length }}/250</small>
            </div>
          </div>

          <!-- ── DETAIL ── -->
          <div class="field-group">
            <label class="field-label">Detail / Sub-text <span class="field-optional">(optional)</span></label>
            <input type="text" class="form-control"
              [(ngModel)]="form.ndetail"
              maxlength="200"
              placeholder="Extra context — e.g. Student ID, amounts, affected classes…">
            <small class="text-muted">{{ (form.ndetail ?? '').length }}/200</small>
          </div>

          <!-- ── ICON PICKER ── -->
          <div class="field-group">
            <label class="field-label">Icon <span class="req">*</span></label>
            @if (formErrors['icon']) { <div class="text-danger small mb-2">{{ formErrors['icon'] }}</div> }
            <div class="icon-picker-grid">
              @for (opt of iconOptions; track opt.value) {
                <button type="button" class="icon-option"
                  [class.selected]="form.icon === opt.value"
                  [title]="opt.label"
                  (click)="form.icon = opt.value">
                  <i class="bi {{ opt.value }}"></i>
                </button>
              }
            </div>
            @if (form.icon) {
              <div class="selected-display mt-2">
                <i class="bi {{ form.icon }} me-2 text-primary"></i>
                <span class="small text-muted">{{ getIconLabel(form.icon) }}</span>
              </div>
            }
          </div>

          <!-- ── COLOUR PICKER ── -->
          <div class="field-group">
            <label class="field-label">Icon Background Colour <span class="req">*</span></label>
            @if (formErrors['icon_bg']) { <div class="text-danger small mb-2">{{ formErrors['icon_bg'] }}</div> }
            <div class="color-picker-row">
              @for (col of colorOptions; track col.value) {
                <button type="button" class="color-swatch"
                  [class.selected]="form.icon_bg === col.value"
                  [style.background]="col.gradient"
                  [title]="col.label"
                  (click)="form.icon_bg = col.value">
                  @if (form.icon_bg === col.value) {
                    <i class="bi bi-check-lg"></i>
                  }
                </button>
              }
            </div>
            <!-- @if (form.icon_bg) {
              <small class="text-muted">{{ getColorLabel(form.icon_bg ?? '') }}</small>
            } -->
          </div>

          <!-- ── CATEGORY + TIME ── -->
          <div class="row g-3">
            <div class="col-md-6">
              <div class="field-group mb-0">
                <label class="field-label">Category <span class="field-optional">(optional)</span></label>
                <select class="form-select" [(ngModel)]="form.category">
                  <option [value]="undefined">— None —</option>
                  @for (cat of categories; track cat.key) {
                    <option [value]="cat.key">{{ cat.label }}</option>
                  }
                </select>
              </div>
            </div>
            <div class="col-md-6">
              <div class="field-group mb-0">
                <label class="field-label">Time Display</label>
                <input type="text" class="form-control"
                  [(ngModel)]="form.ntime"
                  placeholder="e.g. Just now, 5 min ago, 1 hour ago"
                  maxlength="40">
                <small class="text-muted">Relative time shown under the message</small>
              </div>
            </div>
          </div>

          <!-- ── UNREAD TOGGLE ── -->
          <div class="field-group unread-toggle-row">
            <div>
              <div class="field-label mb-0">Mark as Unread</div>
              <small class="text-muted">Unread notifications appear with a blue border and bold text</small>
            </div>
            <label class="notif-toggle-switch">
              <input type="checkbox" [(ngModel)]="form.unread">
              <span class="nts-track"><span class="nts-thumb"></span></span>
              <span class="nts-label" [class.nts-on]="form.unread">
                {{ form.unread ? 'Unread' : 'Read' }}
              </span>
            </label>
          </div>

          <!-- ── FORM ACTIONS ── -->
          <div class="form-actions">
            <button type="button" class="btn btn-outline-secondary px-5" (click)="closeForm()">Cancel</button>
            <button type="button" class="btn notif-btn-primary px-5" (click)="saveForm()">
              <i class="bi {{ isEditMode() ? 'bi-check-circle-fill' : 'bi-plus-circle-fill' }} me-2"></i>
              {{ isEditMode() ? 'Update Notification' : 'Add Notification' }}
            </button>
          </div>

        </div>
      </div>

      <!-- Right: Live Preview -->
      <div class="form-preview-panel">
        <div class="fpp-header">
          <i class="bi bi-eye-fill me-2 text-muted"></i>Live Preview
        </div>
        <div class="fpp-body">
          <p class="fpp-note">This is how the notification will appear in the list.</p>

          <!-- Preview card -->
          @let prev = previewNotif();
          <div class="preview-card" [class.preview-unread]="prev.unread">
            @if (prev.unread) { <div class="preview-dot"></div> }
            <div class="notif-icon-wrap {{ prev.icon_bg }}">
              <i class="bi {{ prev.icon }}"></i>
            </div>
            <div class="notif-content">
              @if (prev.category) {
                <span class="notif-category-badge" [style.color]="getCategoryColor(prev.category)">
                  <i class="bi {{ getCategoryIcon(prev.category) }} me-1" style="font-size:.65rem;"></i>
                  {{ getCategoryLabel(prev.category) }}
                </span>
              }
              <p class="notif-text" [class.text-unread]="prev.unread">{{ prev.ntext }}</p>
              @if (prev.ndetail) { <p class="notif-detail">{{ prev.ndetail }}</p> }
              <div class="notif-meta">
                <i class="bi bi-clock me-1" style="font-size:.65rem;"></i>
                <span>{{ prev.ntime }}</span>
                @if (prev.unread) { <span class="new-badge ms-2">New</span> }
              </div>
            </div>
          </div>

          <!-- Field summary -->
          <div class="preview-summary">
            <div class="ps-row"><span>Icon</span><span>
              <i class="bi {{ prev.icon }} me-1"></i>{{ getIconLabel(prev.icon) }}</span>
            </div>
            <div class="ps-row"><span>Background</span>
              <span>
                <span class="ps-color-dot" [style.background]="getColorGradient(prev.icon_bg)"></span>
                {{ getColorLabel(prev.icon_bg) }}
              </span>
            </div>
            <div class="ps-row"><span>Category</span><span>{{ prev.category ? getCategoryLabel(prev.category) : '—' }}</span></div>
            <div class="ps-row"><span>Status</span>
              <span [class.text-primary]="prev.unread" [class.text-muted]="!prev.unread">
                {{ prev.unread ? 'Unread' : 'Read' }}
              </span>
            </div>
          </div>
        </div>
      </div>

    </div>
  }

  <!-- ===================== LIST VIEW ===================== -->
  @if (!showForm()) {
    <div class="notif-layout">

      <!-- Sidebar -->
      <div class="notif-sidebar">
        <div class="notif-search mb-3">
          <div class="input-group">
            <span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted" style="font-size:.85rem;"></i></span>
            <input type="text" class="form-control border-start-0" placeholder="Search notifications…" [(ngModel)]="searchQuery">
            @if (searchQuery) { <button class="btn btn-outline-secondary" (click)="searchQuery=''"><i class="bi bi-x" style="font-size:.9rem;"></i></button> }
          </div>
        </div>
        <div class="sidebar-section mb-3">
          <div class="ss-label">Quick Filters</div>
          <ul class="filter-list">
            @for (f of [{k:'all',l:'All Notifications',ic:'bi-bell-fill',count:notifications() && notifications().length},{k:'unread',l:'Unread',ic:'bi-circle-fill',count:unreadCount()},{k:'read',l:'Read',ic:'bi-check-circle',count:notifications() && notifications().length - unreadCount()}]; track f.k) {
              <li class="filter-item d-flex justify-content-between" [class.active]="activeFilter() === f.k" (click)="activeFilter.set($any(f.k))">
                <div class="d-flex align-items-center gap-2">
                <i class="bi {{ f.ic }} filter-icon"></i><span>{{ f.l }}</span>
                </div>
                @if (f.count > 0) {
                  <span class="filter-count" [class.count-unread]="f.k==='unread'">{{ f.count }}</span>
                }
              </li>
            }
          </ul>
        </div>
        <div class="sidebar-section">
          <div class="ss-label">By Category</div>
          <ul class="filter-list">
            @for (cat of categories; track cat.key) {
              @let total = categoryCounts().get(cat.key) ?? 0;
              @if (total > 0) {
                <li class="filter-item d-flex justify-content-between" [class.active]="activeFilter() === cat.key" (click)="activeFilter.set(cat.key)">
                  <div class="d-flex align-items-center gap-2">
                    <div class="cat-dot" [style.background]="cat.color"></div>
                    <i class="bi {{ cat.icon }} filter-icon" [style.color]="cat.color"></i>
                    <span>{{ cat.label }}</span>
                  </div>
                  <span class="filter-count" [class.count-unread]="(unreadByCat().get(cat.key) ?? 0) > 0">
                    {{ total }}@if ((unreadByCat().get(cat.key) ?? 0) > 0) { <span class="unread-dot"></span> }
                  </span>
                </li>
              }
            }
          </ul>
        </div>
      </div>

      <!-- Feed -->
      <div class="notif-feed">
        <div class="feed-header">
          <h6 class="feed-title"><i class="bi bi-funnel-fill me-2 text-muted" style="font-size:.85rem;"></i>{{ getFilterLabel() }}</h6>
          <span class="feed-count">{{ filtered() && filtered().length }} notification(s)</span>
        </div>
          @for (group of grouped(); track group.label) {
            <div class="notif-group">
              <div class="group-label">{{ group.label }}</div>

              @for (n of group.items; track n.id) {
                <div class="notif-item" [class.notif-unread]="n.unread"
                  (click)="n.unread && markRead(n.id)">

                  @if (n.unread) { <div class="unread-indicator"></div> }

                  <div class="notif-icon-wrap {{ n.icon_bg }}">
                    <i class="bi {{ n.icon }}"></i>
                  </div>

                  <div class="notif-content">
                    @if (n.category) {
                      <span class="notif-category-badge" [style.color]="getCategoryColor(n.category)">
                        <i class="bi {{ getCategoryIcon(n.category) }} me-1" style="font-size:.65rem;"></i>
                        {{ getCategoryLabel(n.category) }}
                      </span>
                    }
                    <p class="notif-text" [class.text-unread]="n.unread">{{ n.ntext }}</p>
                    @if (n.ndetail) { <p class="notif-detail">{{ n.ndetail }}</p> }
                    <div class="notif-meta">
                      <i class="bi bi-clock me-1" style="font-size:.65rem;"></i>
                      <span>{{ n.ntime }}</span>
                      @if (n.unread) { <span class="new-badge ms-2">New</span> }
                    </div>
                  </div>

                  <div class="notif-actions">
                    <!-- Edit -->
                    <button class="na-btn edit" title="Edit notification"
                      (click)="$event.stopPropagation(); openEditForm(n)">
                      <i class="bi bi-pencil-fill"></i>
                    </button>
                    <!-- Toggle read -->
                    <button class="na-btn" [title]="n.unread ? 'Mark as read' : 'Mark as unread'"
                      (click)="$event.stopPropagation(); toggleUnread(n.id)">
                      <i class="bi" [class.bi-check-circle-fill]="n.unread" [class.bi-circle]="!n.unread"
                        [class.text-primary]="n.unread" [class.text-muted]="!n.unread"></i>
                    </button>
                    <!-- Delete -->
                      <!-- (click)="$event.stopPropagation(); deleteNotification(n.id)" -->
                    <button class="na-btn danger" title="Delete"
                      (click)="deleteClick(n.id);">
                      <i class="bi bi-trash3-fill"></i>
                    </button>
                  </div>
                </div>
              }
            </div>
          } @empty {
            <div class="empty-state">
              <div class="es-icon"><i class="bi bi-bell-slash-fill"></i></div>
              <h5>All clear!</h5>
              <p>
                @if (searchQuery) { No notifications matching "{{ searchQuery }}" }
                @else if (activeFilter() === 'unread') { No unread notifications. }
                @else { No notifications in this category. }
              </p>
              <div class="d-flex gap-2 justify-content-center flex-wrap">
                @if (searchQuery || activeFilter() !== 'all') {
                  <button class="btn btn-outline-secondary btn-sm" (click)="searchQuery=''; activeFilter.set('all')">Show All</button>
                }
                <button class="btn notif-btn-primary btn-sm" (click)="openAddForm()">
                  <i class="bi bi-plus-circle me-1"></i>Add Notification
                </button>
              </div>
            </div>
          }
      </div>

    </div>
  }

</div>
